<template>
  <article>
    <ToolBar />

    <div v-if="$apollo.loading">
      <v-progress-circular
        :size="70"
        :width="7"
        color="blue"
        indeterminate
      ></v-progress-circular>
    </div>

    <v-container fluid v-else>
      <h1 v-if="slo" class="headline">SLO: {{ slo.name }}</h1>

      <v-layout row wrap>
        <v-flex class="text-xs-center" xs12 md3>
          <v-card>
            <div class="subheading">Current Level</div>
            <div>
              <h3 :class="sloPctTextColor" class="display-2">{{ round(slo.currentPercent) }}%</h3>
            </div>
          </v-card>
        </v-flex>

        <v-flex class="text-xs-center" xs12 md3>
          <v-card>
            <div class="subheading">
              Target Level
            </div>
            <div>
              <span class="display-2 grey--text">{{ slo.minPercent }}%</span>
            </div>
          </v-card>
        </v-flex>
        <v-flex class="text-xs-center" xs12 md3>
          <v-card>
            <div class="subheading">
              Errors Remaining
            </div>
            <div>
              <span :class="sloPctTextColor" class="display-2 grey--text">{{ errBudgetDisplay }}</span>
            </div>
          </v-card>
        </v-flex>
        <v-flex class="text-xs-center" xs12 md3>
          <v-card>
            <div class="subheading">SLO Period</div>
            <div>
              <span class="display-2">{{ sloPeriod }}</span>
            </div>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12>
          <v-divider></v-divider>
        </v-flex>
      </v-layout>
    
      <v-layout row wrap>
        <v-flex xs12 md6>
          <v-card height="100%">
            <v-card-title>
              <div class="subheading grey--text">Error Budget History (% Remaining)</div>
            </v-card-title>
            <v-card-text>
              <BudgetHistoryChart :chartData="chartData" :options="chartOptions" />
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 md6>
          <v-card height="100%">
            <v-card-title>
              <div class="subheading grey--text">Incidents</div>
            </v-card-title>
            <v-card-text>
              <div v-for="(incident, idx) of recentIncidents" :key="idx">
                <AlertSummary 
                  :incident="incident"
                  :appName="slo.app.name"
                  :sloName="slo.name"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <br>
      <h1 class="title">SLIs</h1>
      <br>

      <v-layout row wrap>
        <v-flex xs12 md6>
          <SLIDetail :sli="slo.successSLI" :sliType="'Success'" />
        </v-flex>
        <v-flex xs12 md6>
          <SLIDetail :sli="slo.totalSLI" :sliType="'Total'" />
        </v-flex>
      </v-layout>
    </v-container>
  </article>
</template>

<script>
import ToolBar from "@/components/Toolbar.vue";
import BudgetHistoryChart from "@/components/BudgetHistoryChart.js";
import AlertSummary from "@/components/AlertSummary";
import SLIDetail from "@/components/SLIDetail";

import gql from "graphql-tag";
var moment = require("moment");

export default {
  name: "SLO",
  components: {
    ToolBar,
    BudgetHistoryChart,
    AlertSummary,
    SLIDetail
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      rateSloByID: null,
      chartOptions: {
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            display: true,
            ticks: {
              suggestedMin: 0,
              max: 100
            }
          }]
        }
      }
    };
  },
  apollo: {
    rateSloByID: {
      query: gql` query sloQuery($id: Int!) {
        rateSloByID(id: $id) {
          id
          name
          app {
            name
          }
          minPercent
          intervalHours
          currentPercent
          currentErrorBudget {
            totalBudget
            remainingBudget
          }
          errorBudgetHistory {
            time
            totalBudget
            remainingBudget
          }
          incidents {
            severity
            started
            ended
          }
          successSLI {
            id
            sourceURL
            query
            metrics {
              time
              name
              value
              tags {
                key
                value
              }
            }
          }
          totalSLI {
            id
            sourceURL
            query
            metrics {
              time
              name
              value
              tags {
                key
                value
              }
            }
          }
        }
      }`,
      variables() {
        return {
          id: this.id
        };
      }
    }
  },
  methods: {
    round(num) {
      return num.toFixed(2);
    }
  },
  computed: {
    slo() {
      return this.rateSloByID;
    },
    pctBudgetLeft() {
      var remaining = this.slo.currentErrorBudget.remainingBudget;
      var total = this.slo.currentErrorBudget.totalBudget;

      if (total == 0.0) {
        return 0.0;
      }

      return 100.0 * (remaining / total);
    },
    sloStatusColor() {
      var total = this.slo.currentErrorBudget.totalBudget;

      if (total == 0.0) {
        return "grey";
      }

      if (this.pctBudgetLeft > 75.0) {
        return "success";
      }
      if (this.pctBudgetLeft > 30.0) {
        return "warning";
      }
      if (this.pctBudgetLeft <= 30.0) {
        return "error";
      }

      return "grey";
    },
    sloPctTextColor() {
      return this.sloStatusColor + "--text";
    },
    sloPeriod() {
      var days = Math.floor(this.slo.intervalHours / 24);
      var hours = this.slo.intervalHours % 24;
      var period = "";

      if (days === 0 && hours === 0) {
        return "0";
      }

      if (days > 0) {
        period += `${days} days`;
      }
      if (days > 0 && hours > 0) {
        period += ", ";
      }
      if (hours > 0) {
        period += `${hours} hours`;
      }

      return period;
    },
    errBudgetDisplay() {
      let remaining = Math.round(this.slo.currentErrorBudget.remainingBudget);
      let total = Math.round(this.slo.currentErrorBudget.totalBudget);

      if (remaining < 0) {
        return "Over by " + -remaining;
      }

      return `${remaining} / ${total}`;
    },
    historyLabels() {
      return this.slo.errorBudgetHistory.map((b) => {
        return moment(b.time).format("MM/DD/YYYY h:mm:ssa");
      });
    },
    historyValues() {
      return this.slo.errorBudgetHistory.map((b) => {
        return 100 * b.remainingBudget / b.totalBudget;
      });
    },
    chartData() {
      return {
        labels: this.historyLabels,
        datasets: [
          {
            label: "% Budget Left",
            backgroundColor: "#2e6dd1",
            data: this.historyValues
          }
        ]
      };
    },
    recentIncidents() {
      return this.slo.incidents.slice(-7).reverse();
    }
  }
};
</script>
